import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { finagApiUrl } from "../config";
import { Logout } from "../Auth/auth";
import toast from "react-hot-toast";


const getToken = () => {
  const token = localStorage.getItem("token");
  return token;
};

// Get store management statistics
export const getStoreManagementStats = createAsyncThunk(
  "storeManagement/getStats",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const url = `${finagApiUrl}inventory/stores/store-counts/`;

      const res = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (res.status === 200) {
        return res.data;
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        dispatch(Logout());
        return rejectWithValue({ message: "Unauthorized access" });
      } else if (error?.response?.data) {
        return rejectWithValue(error.response.data);
      } else {
        return rejectWithValue({
          message: "An error occurred while fetching store management stats",
        });
      }
    }
  }
);

// Get store stock value data
export const getStoreStockValue = createAsyncThunk(
  "storeManagement/getStockValue",
  async (params = {}, { rejectWithValue, dispatch }) => {
    try {
      const { filter_period = "1month" } = params;
      const url = `${finagApiUrl}inventory/stores/stock-sale-graph/?filter_period=${filter_period}`;

      const res = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (res.status === 200) {
        return res.data;
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        dispatch(Logout());
        return rejectWithValue({ message: "Unauthorized access" });
      } else if (error?.response?.data) {
        return rejectWithValue(error.response.data);
      } else {
        return rejectWithValue({
          message: "An error occurred while fetching store stock value data",
        });
      }
    }
  }
);

// Get store activity logs
export const getStoreActivityLogs = createAsyncThunk(
  "storeManagement/getActivityLogs",
  async (params = {}, { rejectWithValue }) => {
    try {
      const { page = 1, page_size = 5 } = params;
      const response = await axios.get(
        `/store-management/activity-logs?page=${page}&page_size=${page_size}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch activity logs" }
      );
    }
  }
);



// Add store manager
export const addStoreManager = createAsyncThunk(
  "storeManagement/addStoreManager",
  async (storeManagerData, { rejectWithValue, dispatch }) => {
    try {
      const url = `${finagApiUrl}inventory/stores/create-manager/`;

      const response = await axios.post(url, storeManagerData, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.status === 200 || response.status === 201) {
        toast.success("Store manager added successfully!");
        return response.data;
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        dispatch(Logout());
        return rejectWithValue({ message: "Unauthorized access" });
      } else if (error?.response?.data) {
        return rejectWithValue(error.response.data);
      } else {
        toast.error("An error occurred while adding store manager");
        return rejectWithValue({
          message: "An error occurred while adding store manager",
        });
      }
    }
  }
);


// Add store
export const addStore = createAsyncThunk(
  "storeManagement/addStore",
  async (storeData, { rejectWithValue, dispatch }) => {
    try {
      const url = `${finagApiUrl}inventory/stores/`;

      const response = await axios.post(url, storeData, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.status === 200 || response.status === 201) {
        toast.success("Store added successfully!");
        return response.data;
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        dispatch(Logout());
        return rejectWithValue({ message: "Unauthorized access" });
      } else if (error?.response?.data) {
        return rejectWithValue(error.response.data);
      } else {
        toast.error("An error occurred while adding store");
        return rejectWithValue({
          message: "An error occurred while adding store",
        });
      }
    }
  }
);

// Get all stores
export const getAllStores = createAsyncThunk(
  "storeManagement/getAllStores",
  async (params = {}, { rejectWithValue, dispatch }) => {
    try {
      const { page = 1, page_size = 10, search = "", status = "" } = params;

      let url = `${finagApiUrl}inventory/stores/locations`;

      // Add query parameters if provided
      const queryParams = new URLSearchParams();
      if (page) queryParams.append("page", page);
      if (page_size) queryParams.append("page_size", page_size);
      if (search) queryParams.append("search", search);
      if (status) queryParams.append("status", status);

      if (queryParams.toString()) {
        url += `?${queryParams.toString()}`;
      }

      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.status === 200) {
        // Transform the API response to match the expected format
        const transformedData = response.data.data.map((store) => ({
          id: store.id,
          name: store.name,
          location:
            `${store.state?.name || ""}, ${store.lga?.name || ""}`
              .replace(", ,", "")
              .trim() || "Unknown Location",
          address: store.address || "No address provided",
          state: store.state?.name || "Unknown State",
          lga: store.lga?.name || "Unknown LGA",
          manager: store.store_manager
            ? `${store.store_manager.first_name} ${store.store_manager.last_name}`.trim()
            : "No Manager Assigned",
          manager_phone: store.store_manager?.phone_number || "N/A",
          manager_email: "N/A", // Not available in current API response
          status: store.is_active ? "Active" : "Inactive",
          stock_value: store.stock_value, // This would need to come from another endpoint
          total_products: 0, // This would need to come from another endpoint
          low_stock_items: 0, // This would need to come from another endpoint
          out_of_stock_items: 0, // This would need to come from another endpoint
          created_at: store.created,
          updated_at: store.updated,
        }));

        // Return the properly structured response with server pagination
        return {
          data: transformedData,
          total_items:
            response.data.pagination?.total_count || transformedData.length,
          current_page: response.data.pagination?.current_page || page,
          page_size: response.data.pagination?.page_size || page_size,
          total_pages:
            response.data.pagination?.total_pages ||
            Math.ceil(
              (response.data.pagination?.total_count ||
                transformedData.length) / page_size
            ),
        };
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        dispatch(Logout());
        return rejectWithValue({ message: "Unauthorized access" });
      } else if (error?.response?.data) {
        toast.error("Failed to fetch stores");
        return rejectWithValue(error.response.data);
      } else {
        toast.error("An error occurred while fetching stores");
        return rejectWithValue({
          message: "An error occurred while fetching stores",
        });
      }
    }
  }
);

// Get store management statistics
export const getAllStoresStats = createAsyncThunk(
  "storeManagement/getAllStoresStats",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const url = `${finagApiUrl}inventory/stores/statistics/`;

      const res = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (res.status === 200) {
        return res.data;
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        dispatch(Logout());
        return rejectWithValue({ message: "Unauthorized access" });
      } else if (error?.response?.data) {
        return rejectWithValue(error.response.data);
      } else {
        return rejectWithValue({
          message: "An error occurred while fetching store stats",
        });
      }
    }
  }
);

// Get all stores without pagination (for dropdowns, selectors, etc.)
export const getAllStoreLocations = createAsyncThunk(
  "storeManagement/getAllStoreLocations",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const url = `${finagApiUrl}inventory/stores/all-locations`;

      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.status === 200) {
        return response.data;
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        dispatch(Logout());
        return rejectWithValue({ message: "Unauthorized access" });
      } else if (error?.response?.data) {
        toast.error("Failed to fetch store locations");
        return rejectWithValue(error.response.data);
      } else {
        toast.error("An error occurred while fetching store locations");
        return rejectWithValue({
          message: "An error occurred while fetching store locations",
        });
      }
    }
  }
);

// Get all store managers
export const getAllStoreManagers = createAsyncThunk(
  "storeManagement/getAllStoreManagers",
  async (params = {}, { rejectWithValue, dispatch }) => {
    try {
      const {
        page = 1,
        page_size = 10,
        search = "",
        invite_status = "",
      } = params;

      let url = `${finagApiUrl}inventory/stores/managers`;

      // Add query parameters if provided
      const queryParams = new URLSearchParams();
      if (page) queryParams.append("page", page);
      if (page_size) queryParams.append("page_size", page_size);
      if (search) queryParams.append("search", search);
      if (invite_status) queryParams.append("invite_status", invite_status);

      if (queryParams.toString()) {
        url += `?${queryParams.toString()}`;
      }

      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.status === 200) {
        // Transform the API response to match the expected format
        const transformedData = response.data.data.map((manager) => ({
          id: manager.store_manager.id,
          name: `${manager.store_manager.first_name} ${manager.store_manager.last_name}`.trim(),
          phone: manager.phone_number || "N/A",
          assigned_store: manager.assigned_store.name,
          state: manager.state,
          date_assigned: new Date(manager.date_assigned).toLocaleDateString(
            "en-GB",
            {
              day: "2-digit",
              month: "2-digit",
              year: "2-digit",
            }
          ),
          last_active: manager.last_active
            ? (() => {
                const lastActiveDate = new Date(manager.last_active);
                const now = new Date();
                const diffInMs = now - lastActiveDate;
                const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
                const diffInDays = Math.floor(diffInHours / 24);

                if (diffInHours < 1) {
                  return "Just now";
                } else if (diffInHours < 24) {
                  return `${diffInHours} hour${
                    diffInHours === 1 ? "" : "s"
                  } ago`;
                } else if (diffInDays < 7) {
                  return `${diffInDays} day${diffInDays === 1 ? "" : "s"} ago`;
                } else if (diffInDays < 30) {
                  const weeks = Math.floor(diffInDays / 7);
                  return `${weeks} week${weeks === 1 ? "" : "s"} ago`;
                } else {
                  const months = Math.floor(diffInDays / 30);
                  return `${months} month${months === 1 ? "" : "s"} ago`;
                }
              })()
            : "No activity",
          store_id: manager.assigned_store.id,
          store_address: manager.assigned_store.address,
          store_lga: manager.assigned_store.lga,
        }));

        // Return the properly structured response with server pagination
        return {
          data: transformedData,
          total_items: response.data.pagination?.total_items,
          current_page: page,
          page_size: page_size,
          total_pages: Math.ceil(
            response.data.pagination?.total_items / page_size
          ),
        };
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        dispatch(Logout());
        return rejectWithValue({ message: "Unauthorized access" });
      } else if (error?.response?.data) {
        toast.error("Failed to fetch store managers");
        return rejectWithValue(error.response.data);
      } else {
        toast.error("An error occurred while fetching store managers");
        return rejectWithValue({
          message: "An error occurred while fetching store managers",
        });
      }
    }
  }
);

// Get Store Manager Orders
export const getStoreManagerOrders = createAsyncThunk(
  "storeManagement/getStoreManagerOrders",
  async ({page =1, page_size = 10, filter, days, search}, { rejectWithValue, dispatch }) => {
      try{
      

        const queryParams = new URLSearchParams()
        queryParams.append("page", page)
        queryParams.append("page_size", page_size)

        if(days) queryParams.append("days", days)
        if(filter) queryParams.append("filter", filter)
        if(search) queryParams.append("search", search)

          const url = `${finagApiUrl}sharp-input-orders/recent-orders/?${queryParams.toString()}`

          const res = await axios.get(url, {
           headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
          })

          if(res.status === 200){
            return res.data
          }
          
      }
      catch(error){
        if (error.response && error.response.status === 401) {
          dispatch(Logout());
          return rejectWithValue({ message: "Unauthorized access" });
        } else if (error?.response?.data) {
          toast.error("Failed to fetch store managers");
          return rejectWithValue(error.response.data);
        } else {
          toast.error("An error occurred while fetching store managers");
          return rejectWithValue({
            message: "An error occurred while fetching store managers",
          });
        }
      }
  }
)

// Get all stock requests
export const getAllStockRequests = createAsyncThunk(
  "storeManagement/getAllStockRequests",
  async (params = {}, { rejectWithValue, dispatch }) => {
    try {
      const { page = 1, page_size = 10, search = "", status = "" } = params;

      // Build the URL with query parameters
      let url = `${finagApiUrl}inventory/stock-requests/all-requests`;
      const queryParams = new URLSearchParams();

      // Add pagination parameters
      queryParams.append("page", page.toString());
      queryParams.append("page_size", page_size.toString());

      // Add search parameter if provided
      if (search && search.trim() !== "") {
        queryParams.append("search", search.trim());
      }

      // Add status filter parameter if provided and not "All"
      if (status && status.trim() !== "" && status !== "All") {
        // Map frontend status values to backend expected values
        let backendStatus = status;
        switch (status) {
          case "PENDING":
            backendStatus = "pending";
            break;
          case "APPROVED":
            backendStatus = "approved";
            break;
          case "Dispatched":
            backendStatus = "dispatched";
            break;
          case "Confirmed":
            backendStatus = "fulfilled";
            break;
          case "Rejected":
            backendStatus = "cancel";
            break;
          case "PROCESSING":
            backendStatus = "processing";
            break;
          default:
            backendStatus = status.toLowerCase();
        }
        queryParams.append("status_filter", backendStatus);
      }
      // If status is "All" or empty, don't add status_filter parameter

      // Append query parameters to URL
      if (queryParams.toString()) {
        url += `?${queryParams.toString()}`;
      }

      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
      });

      let allRequests = response.data.data || [];

      console.log(allRequests);

      // Map the backend data to match frontend expectations
      const mappedRequests = allRequests.map((request) => ({
        id: request.request_id,
        internal_id: request.id,
        date_requested: new Date(request.created).toLocaleDateString(),
        requested_by:
          request.requested_by?.first_name && request.requested_by?.last_name
            ? `${request.requested_by.first_name} ${request.requested_by.last_name}`
            : request.requested_by?.username || "Unknown",
        product_name:
          request.product_variant?.product_name || "Unknown Product",
        variant_data: {
          type: request.product_variant?.variant_data?.type || "N/A",
          value_unit:
            request.product_variant?.variant_data?.value &&
            request.product_variant?.variant_data?.unit
              ? `${request.product_variant.variant_data.value}${request.product_variant.variant_data.unit}`
              : "N/A",
        },
        quantity: request.quantity_requested,
        approval_status: request.approval_status,
        fulfillment_status: request.fulfillment_status || "--",
        request_type: request.request_type,
        requesting_location: request.requesting_location?.name || "Unknown",
        original: request,
      }));

      const totalItems = response.data.total_items || mappedRequests.length;
      const totalPages =
        response.data.total_pages || Math.ceil(totalItems / page_size);

      // Include tab counts with "All" count
      const tabCounts = response.data.length || {
        All: 0, // Add total count for "All" tab
        PENDING: 0,
        PROCESSING: 0,
        APPROVED: 0,
        Dispatched: 0,
        Confirmed: 0,
        Rejected: 0,
      };

      return {
        data: mappedRequests,
        total_items: totalItems,
        current_page: page,
        page_size: page_size,
        total_pages: totalPages,
        tab_counts: tabCounts,
      };
    } catch (error) {
      if (error.response && error.response.status === 401) {
        dispatch(Logout());
        return rejectWithValue({ message: "Unauthorized access" });
      } else {
        toast.error("Failed to fetch stock requests");
        return rejectWithValue({
          message: "An error occurred while fetching stock requests",
        });
      }
    }
  }
);
// Get stock request by ID
export const getStockRequestById = createAsyncThunk(
  "storeManagement/getStockRequestById",
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const url = `${finagApiUrl}inventory/stock-requests/${id}`;

      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
      });

      const requestData = response.data;

      console.log(requestData);

      if (!requestData) {
        return rejectWithValue({ message: "No data returned from API" });
      }

      // Map the backend data to match frontend expectations
      const mappedRequest = {
        id: requestData.request_id,
        internal_id: requestData.id,
        date_requested: new Date(requestData.created).toLocaleDateString(),
        requested_by:
          requestData.requested_by?.first_name &&
          requestData.requested_by?.last_name
            ? `${requestData.requested_by.first_name} ${requestData.requested_by.last_name}`
            : requestData.requested_by?.username || "Unknown",
        product_name:
          requestData.product_variant?.product_name || "Unknown Product",
        variant_data: {
          type: requestData.product_variant?.variant_data?.type || "N/A",
          value: requestData.product_variant?.variant_data?.value || "N/A",
          unit: requestData.product_variant?.variant_data?.unit || "N/A",
          value_unit:
            requestData.product_variant?.variant_data?.value &&
            requestData.product_variant?.variant_data?.unit
              ? `${requestData.product_variant.variant_data.value}${requestData.product_variant.variant_data.unit}`
              : "N/A",
        },
        quantity: requestData.quantity_requested,
        approval_status: requestData.approval_status,
        fulfillment_status: requestData.fulfillment_status || "--",
        fulfillments: requestData.fulfillments || [],
        request_type: requestData.request_type,
        requesting_location: requestData.requesting_location?.name || "Unknown",
        // Additional fields that might be useful for the details page
        requester_phone: requestData.requested_by?.phone || "+2348123456789",
        delivery_address: requestData.delivery_address || "-",
        estimated_delivery_date: requestData.estimated_delivery_date || null,
        delivery_type: requestData.delivery_type || "--",
        notes: requestData.notes || [],
        // Keep original data for any additional processing
        original: requestData,
      };

      return {
        data: mappedRequest,
      };
    } catch (error) {
      console.error("Error fetching stock request:", error); // Add this for debugging
      if (error.response && error.response.status === 401) {
        dispatch(Logout());
        return rejectWithValue({ message: "Unauthorized access" });
      } else if (error.response && error.response.status === 404) {
        return rejectWithValue({ message: "Stock request not found" });
      } else {
        toast.error("Failed to fetch stock request details");
        return rejectWithValue({
          message: "An error occurred while fetching stock request details",
        });
      }
    }
  }
);

// Get store analytics/top stores
export const getStoreAnalytics = createAsyncThunk(
  "storeManagement/getStoreAnalytics",
  async (params = {}, { rejectWithValue, dispatch }) => {
    try {
      const { page = 1, page_size = 100, from_date, to_date } = params;

      let url = `${finagApiUrl}inventory/stores/analytics`;

      // Add query parameters
      const queryParams = new URLSearchParams();
      if (page) queryParams.append("page", page);
      if (page_size) queryParams.append("page_size", page_size);
      if (from_date) queryParams.append("from_date", from_date);
      if (to_date) queryParams.append("to_date", to_date);

      if (queryParams.toString()) {
        url += `?${queryParams.toString()}`;
      }

      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.status === 200) {
        // Transform the API response to match the expected format
        const transformedData = response.data.results.map((item) => ({
          id: item.store.id,
          storeName: item.store.name,
          storeManager: item.store_manager
            ? `${item.store_manager.name || ""}`.trim()
            : "No Manager Assigned",
          topProduct: item.top_product || "Unknown Product",
          productsSold: item.products_sold_quantity || 0,
          totalSales: `₦${parseFloat(
            item.total_sales_value || 0
          ).toLocaleString()}`,
          location: `${item.store.state}, ${item.store.lga}`,
          address: item.store.address,
        }));

        return {
          data: transformedData,
          total_items: response.data.pagination.total_items,
          current_page: page,
          page_size: page_size,
          total_pages: Math.ceil(
            response.data.pagination.total_items / page_size
          ),
        };
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        dispatch(Logout());
        return rejectWithValue({ message: "Unauthorized access" });
      } else if (error?.response?.data) {
        toast.error("Failed to fetch store analytics");
        return rejectWithValue(error.response.data);
      } else {
        toast.error("An error occurred while fetching store analytics");
        return rejectWithValue({
          message: "An error occurred while fetching store analytics",
        });
      }
    }
  }
);

// Get restock requests
export const getRestockRequests = createAsyncThunk(
  "storeManagement/getRestockRequests",
  async (params = {}, { rejectWithValue, dispatch }) => {
    try {
      const { page = 1, page_size = 100, from_date, to_date } = params;

      let url = `${finagApiUrl}inventory/stock-requests/restock-requests?page=${page}&page_size=${page_size}`;

      if (from_date && to_date) {
        url += `&from_date=${from_date}&to_date=${to_date}`;
      }

      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.status === 200) {
        console.log("Restock requests response:", response.data);
        // Transform the data to match the component's expected format
        const transformedData = response.data.data.map((item) => ({
          id: item.id,
          storeName: item.requesting_location?.name || "Unknown Store",
          product: item.product_variant?.product_name || "Unknown Product",
          requestedQty: item.quantity_requested,
          currentStock: item.quantity_fulfilled,
          requestDate: item.created
            ? new Date(item.created).toISOString().split("T")[0]
            : "",
          priority: item.approval_status === "APPROVED" ? "Medium" : "High", // Map based on status
          requestId: item.request_id,
          fulfillmentStatus: item.fulfillment_status,
          approvalStatus: item.approval_status,
          requestedBy: item.requested_by?.full_name || "Unknown",
          approvedBy: item.approved_by?.full_name || null,
          fulfillmentPercentage: item.fulfillment_percentage || 0,
        }));

        return {
          data: transformedData,
          total_items: response.data.pagination?.total_items,
          current_page: page,
          page_size: page_size,
          total_pages: response.data.pagination
            ? Math.ceil(response.data.pagination.total_items / page_size)
            : 1,
        };
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        dispatch(Logout());
        return rejectWithValue({ message: "Unauthorized access" });
      } else if (error?.response?.data) {
        toast.error("Failed to fetch restock requests");
        return rejectWithValue(error.response.data);
      } else {
        toast.error("An error occurred while fetching restock requests");
        return rejectWithValue({
          message: "An error occurred while fetching restock requests",
        });
      }
    }
  }
);

// Get stock transfers
export const getStockTransfers = createAsyncThunk(
  "storeManagement/getStockTransfers",
  async (params = {}, { rejectWithValue, dispatch }) => {
    try {
      const { page = 1, page_size = 100, from_date, to_date } = params;

      let url = `${finagApiUrl}inventory/stock-requests/stock-transfers?page=${page}&page_size=${page_size}`;

      if (from_date && to_date) {
        url += `&from_date=${from_date}&to_date=${to_date}`;
      }

      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.status === 200) {
        console.log("Stock transfers response:", response.data);
        // Transform the data to match the component's expected format
        const transformedData = response.data.data.map((item) => ({
          id: item.id,
          requestId: item.request_id,
          fromStore:
            item.source_locations && item.source_locations.length > 0
              ? item.source_locations[0].location?.name || "Unknown Store"
              : "Unknown Store",
          toStore: item.requesting_location?.name || "Unknown Store",
          product: item.product_variant?.product_name || "Unknown Product",
          variant: item.product_variant?.variant_display || "",
          quantity: item.quantity_requested,
          quantityFulfilled: item.quantity_fulfilled,
          transferDate: item.created
            ? new Date(item.created).toISOString().split("T")[0]
            : "",
          status: item.fulfillment_status,
          fulfillmentPercentage: item.fulfillment_percentage || 0,
          sourceLocations: item.source_locations || [],
        }));

        return {
          data: transformedData,
          total_items: response.data.pagination?.total_items,
          current_page: page,
          page_size: page_size,
          total_pages: response.data.pagination
            ? Math.ceil(response.data.pagination.total_items / page_size)
            : 1,
        };
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        dispatch(Logout());
        return rejectWithValue({ message: "Unauthorized access" });
      } else if (error?.response?.data) {
        toast.error("Failed to fetch stock transfers");
        return rejectWithValue(error.response.data);
      } else {
        toast.error("An error occurred while fetching stock transfers");
        return rejectWithValue({
          message: "An error occurred while fetching stock transfers",
        });
      }
    }
  }
);

// Get low stock items
export const getLowStockItems = createAsyncThunk(
  "storeManagement/getLowStockItems",
  async (params = {}, { rejectWithValue, dispatch }) => {
    try {
      const { page = 1, page_size = 100, from_date, to_date } = params;

      let url = `${finagApiUrl}inventory/stores/low-stock?page=${page}&page_size=${page_size}`;

      if (from_date && to_date) {
        url += `&from_date=${from_date}&to_date=${to_date}`;
      }

      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.status === 200) {
        console.log("Low stock items response:", response.data);
        // Transform the data to match the component's expected format
        const transformedData = response.data.data.map((item) => ({
          id: `${item.store.id}-${item.product.id}`, // Create unique ID
          storeName: item.store.name,
          storeAddress: item.store.address,
          product: item.product.name,
          variantData: item.variant_data,
          variantDisplay: item.variant_data
            ? `${item.variant_data.type || ""} ${
                item.variant_data.value || ""
              }${item.variant_data.unit || ""} ${
                item.variant_data.packaging || ""
              }`.trim()
            : "",
          currentStock: item.current_stock,
          minimumStock: 20, // This might need to come from another field if available
          lastUpdated: new Date().toISOString().split("T")[0], // Current date as placeholder
        }));

        return {
          data: transformedData,
          total_items: response.data.pagination?.total_items,
          current_page: page,
          page_size: page_size,
          total_pages: response.data.pagination
            ? Math.ceil(response.data.pagination.total_items / page_size)
            : 1,
        };
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        dispatch(Logout());
        return rejectWithValue({ message: "Unauthorized access" });
      } else if (error?.response?.data) {
        toast.error("Failed to fetch low stock items");
        return rejectWithValue(error.response.data);
      } else {
        toast.error("An error occurred while fetching low stock items");
        return rejectWithValue({
          message: "An error occurred while fetching low stock items",
        });
      }
    }
  }
);

//Create a request note
export const createRequestNote = createAsyncThunk(
  "storeManagement/createRequestNote",
  async (noteData, { rejectWithValue, dispatch }) => {
    const { requestId, note } = noteData;
    const url = `${finagApiUrl}inventory/stock-requests/${requestId}/add-note/`;

    try {
      const response = await axios.post(url, note, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.status === 201) {
        toast.success("Note added successfully!");
        console.log(response.data);
        return response.data;
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        dispatch(Logout());
        return rejectWithValue({ message: "Unauthorized access" });
      } else if (error?.response?.data) {
        return rejectWithValue(error.response.data);
      } else {
        toast.error("An error occurred while adding note");
        return rejectWithValue({
          message: "An error occurred while adding note",
        });
      }
    }
  }
);

export const getStockRecord = createAsyncThunk(
  " storeManagement/getStockRecord",
  async (params = {}, { rejectWithValue, dispatch }) => {
    try {
      const { productVariantId, locationId, filter } = params;
      const url = `${finagApiUrl}inventory/stock-requests/available-stock?product_variant_id=${productVariantId}&location_id=${locationId}&filter=${filter}`;

      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.status === 200) {
        return response.data;
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        dispatch(Logout());
        return rejectWithValue({ message: "Unauthorized access" });
      } else if (error?.response?.data) {
        return rejectWithValue(error.response.data);
      } else {
        toast.error("An error occurred while fetching stock record");
        return rejectWithValue({
          message: "An error occurred while fetching stock record",
        });
      }
    }
  }
);

export const getStockNotes = createAsyncThunk(
  "storeManagement/getStockNotes",
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const url = `${finagApiUrl}inventory/stock-requests/${id}/notes/`;

      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.status === 200) {
        return response.data;
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        dispatch(Logout());
        return rejectWithValue({ message: "Unauthorized access" });
      } else if (error?.response?.data) {
        return rejectWithValue(error.response.data);
      } else {
        toast.error("An error occurred while fetching notes");
        return rejectWithValue({
          message: "An error occurred while fetching notes",
        });
      }
    }
  }
);

// Get timeline for a stock request
export const getStockRequestTimeline = createAsyncThunk(
  "storeManagement/getStockRequestTimeline",
  async ({ requestId }, { rejectWithValue, dispatch }) => {
    try {
      const url = `${finagApiUrl}inventory/stock-requests/${requestId}/timeline`;

      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.status === 200) {
        return response.data;
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        dispatch(Logout());
        return rejectWithValue({ message: "Unauthorized access" });
      } else if (error?.response?.data) {
        return rejectWithValue(error.response.data);
      } else {
        toast.error("An error occurred while fetching timeline");
        return rejectWithValue({
          message: "An error occurred while fetching timeline",
        });
      }
    }
  }
);

// Approve or reject a stock request (shared endpoint and action)
export const approveStockRequest = createAsyncThunk(
  "storeManagement/approveStockRequest",
  async ({ requestId, payload }, { rejectWithValue, dispatch }) => {
    try {
      const url = `${finagApiUrl}inventory/stock-requests/${requestId}/approve/`;

      const response = await axios.post(url, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.status === 200 || response.status === 201) {
        toast.success(
          payload.action === "approve"
            ? "Stock request approved successfully!"
            : "Stock request rejected successfully!"
        );
        return response.data;
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        dispatch(Logout());
        return rejectWithValue({ message: "Unauthorized access" });
      } else if (error?.response?.data) {
        toast.error(
          payload.action === "approve"
            ? "Failed to approve stock request"
            : "Failed to reject stock request"
        );
        return rejectWithValue(error.response.data);
      } else {
        toast.error("An error occurred while processing stock request");
        return rejectWithValue({
          message: "An error occurred while processing stock request",
        });
      }
    }
  }
);

export const dispatchStockRequest = createAsyncThunk(
  "storeManagement/dispatchStockRequest",
  async ({ requestId, payload }, { rejectWithValue, dispatch }) => {
    try {
      const url = `${finagApiUrl}inventory/stock-requests/${requestId}/dispatch/`;

      const response = await axios.post(url, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.status === 200 || response.status === 201) {
        toast.success("Stock request dispatched successfully!");
        return response.data;
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        dispatch(Logout());
        return rejectWithValue({ message: "Unauthorized access" });
      } else if (error?.response?.data) {
        toast.error("Failed to dispatch stock request");
        return rejectWithValue(error.response.data);
      } else {
        toast.error("An error occurred while dispatching stock request");
        return rejectWithValue({
          message: "An error occurred while dispatching stock request",
        });
      }
    }
  }
);

export const confirmStockRequest = createAsyncThunk(
  "storeManagement/confirmStockRequest",
  async ({ requestId, payload }, { rejectWithValue, dispatch }) => {
    try {
      const url = `${finagApiUrl}inventory/stock-requests/${requestId}/confirm/`;

      const response = await axios.post(url, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.status === 200 || response.status === 201) {
        toast.success("Stock request confirmed successfully!");
        return response.data;
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        dispatch(Logout());
        return rejectWithValue({ message: "Unauthorized access" });
      } else if (error?.response?.data) {
        toast.error("Failed to confirm stock request");
        return rejectWithValue(error.response.data);
      } else {
        toast.error("An error occurred while confirming stock request");
        return rejectWithValue({
          message: "An error occurred while confirming stock request",
        });
      }
    }
  }
);
